import { Icon, type IconName } from "./Icon";
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent } from "react";
import {
  axisLabel, axisUnitLabel, fieldsFor, fieldView, fullDomain, headlineCaption, hint, initialScrollPosition,
  isIntegral, isUnlocked, labelDates, labelRule, nearest, numberText, personalRecord, prLine, RANGES, rangeDays,
  rangeLongLabel, resolvedField, resolvedRange, sameDay, scrollPosition, seriesPoints, sessionDay, showsDots, startOfDay, summary,
  inUnit, unitFor,
  tenureDays, yDomain, yTickCount, allowed, emptyReason, isScrollable, isShown, labelSpanDays, visibleLength, type Field, type Range,
  axisLabels, plotX, scrollBounds, summaryWindow, visibleWindow, windowed, oneRMNeedsBodyweight,
} from "../lib/progressChart";
import {
  DATE_BAND, PLOT_HEIGHT, TICK, AXIS_GAP, areaPath, frame, monotonePath, xScale, yScale, yTicks,
} from "../lib/progressChartGeometry";
import { readoutLine, type ExerciseHistory } from "../lib/progressHistory";
import { VideoThumb } from "./VideoThumb";
import { ENGINE_RULE, NO_BODYWEIGHT_COACH, ruleBodyIsLoad, type OneRMRule, type WeighIn } from "../lib/oneRepMax";
import { ClipPlayButton } from "./ClipPlayer";
import { clipsOfLine } from "../lib/setClips";
import type { Clip } from "../routes/useProgressHistory";
import { readPref, writePref } from "../lib/viewPrefs";
import { TestedMaxBar } from "./TestedMaxBar";

/* 🔴 W72 STEP 4 — THE APP'S PROGRESS CHART ON THE WEB, drawn from `ExerciseProgressDetailView` line by line (his own
 * two phone stills of 2026-09-25 00:21 are the reference). EVERY NUMBER comes from `lib/progressChart.ts`, the twin
 * proven against the Swift; this file only places them. His rules of 2026-09-02 and 2026-08-25, as the phone has them:
 * one dot per session day · the readout gathers every entry of the tapped day · the range spelled out as plain accent
 * text (only the windows this exercise's span can fill) · the PR follows the chosen field and a click on it slides the
 * chart to it · the video square by the title · the date labels by his density rule · dots only up to a month.
 * THE GESTURES BY REGION, AS THE MOUSE'S: moving over the PLOT scrubs (the cursor follows the pointer) and a click
 * selects the nearest session — the phone's "release snaps"; dragging or wheeling on the DATE STRIP moves through
 * time, one window wide, resting on a midnight. The keyboard: ← → step through the sessions, Esc clears.
 * The web departures, named: the field chip is an OUTLINE (Proposal 7 rule 2 — the accent is a line, not a fill),
 * and the menus are popovers, not iOS menus. Colour, sizes and the curve are the phone's. */

const DAY = 86_400_000;
/* W140 — the range and the field are the COACH's remembered choices (`viewPrefs`: per coach, never throws; the pre-W140
   flat keys `progress.range` / `progress.field` are read there as a fallback). */
const RANGE_KEY = "progressRange";
const FIELD_KEY = "progressField";
const readStored = (key: typeof RANGE_KEY | typeof FIELD_KEY): string | null => readPref(key);
const writeStored = (key: typeof RANGE_KEY | typeof FIELD_KEY, value: string) => { writePref(key, value); };

export type ChartProps = {
  history: ExerciseHistory;
  /** The coach row's ticked views (empty = every one the data offers). */
  progressViews: readonly string[];
  demoVideoUrl: string | null;
  thumbnailUrl: string | null;
  now: number;
  onClose: () => void;
  /** For stills: open the field menu at first paint. */
  initialFieldMenu?: boolean;
  notesByInstance?: ReadonlyMap<string, readonly { setNumber: number | null; text: string }[]>;
  /** The clips the athlete chose to save, per session-exercise — the readout plays the selected day's. */
  clipsByInstance?: ReadonlyMap<string, readonly Clip[]>;
  /** W125 — the coach row's `1RM` switch (`ENGINE_RULE` for an exercise with no coach row) and the client's weigh-ins. */
  oneRM?: OneRMRule;
  weighIns?: readonly WeighIn[];
  /** W136 B2 S3r5 P2 — the exercise's distance field is `distance_km`: a distance view says km (`unitFor` / `perUnit`). */
  distanceKm?: boolean;
  /** W125 part 2 (b) — whose tested max the sheet reads and writes (the coach's client); absent = no tested-max line. */
  testedMaxClientId?: string;
};

export function ProgressChart({ history, progressViews, demoVideoUrl, thumbnailUrl, now, onClose, initialFieldMenu, notesByInstance, clipsByInstance,
                                oneRM = ENGINE_RULE, weighIns = [], distanceKm = false, testedMaxClientId }: ChartProps) {
  const entries = history.entries;
  const tenure = tenureDays(entries);
  const [storedRange, setStoredRange] = useState<Range | null>(() => {
    const v = readStored(RANGE_KEY);
    return (RANGES as readonly string[]).includes(v ?? "") ? (v as Range) : null;
  });
  const [storedField, setStoredField] = useState<Field | null>(() => readStored(FIELD_KEY) as Field | null);
  const range = resolvedRange(storedRange, tenure);
  const days = rangeDays(range);
  const available = allowed(fieldsFor(entries, oneRM), progressViews, oneRM);
  const field = resolvedField(storedField, available);
  // W136 B2 S3r5 P2 — the series in the field's unit (`inUnit`, the Swift's twin): no arithmetic on values here.
  const points = useMemo(() => (field === null ? [] : inUnit(seriesPoints(field, entries, oneRM, weighIns), field, distanceKm)),
    [field, entries, oneRM, weighIns, distanceKm]);
  // W125 — an ADDED load is said with its `+`; with the body as load and no weigh-in, the chart says so instead of a number.
  const plus = field === "oneRM" && ruleBodyIsLoad(oneRM) ? "+" : "";
  const needsBodyweight = field === "oneRM" && oneRMNeedsBodyweight(entries, oneRM, weighIns);
  const dates = points.map((p) => p.date);

  const [selected, setSelected] = useState<number | null>(null);
  const [scrub, setScrub] = useState<number | null>(null);
  const [scroll, setScroll] = useState(() => initialScrollPosition(days, dates, now));
  const [menu, setMenu] = useState<"range" | "field" | null>(initialFieldMenu === true ? "field" : null);
  const box = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(560);
  const titleId = useId();

  /* W121 (Fable's ruling E): a record tapped OUTSIDE a fixed window switches to `All` centred on it — the centre waits here
     for the range change's own re-seed. */
  const pendingCentre = useRef<number | null>(null);
  useEffect(() => {
    const c = pendingCentre.current;
    pendingCentre.current = null;
    setScroll(c !== null ? scrollPosition(c, days, dates, now) : initialScrollPosition(days, dates, now)); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, field]);
  /* An opened chart — by a click on its row or by a link naming the lift — is brought into view once, on mount:
     a link to a lift that opens off-screen reads as a link that did nothing. `nearest` never jumps a visible chart. */
  const root = useRef<HTMLElement>(null);
  useEffect(() => { root.current?.scrollIntoView({ block: "nearest" }); }, []);
  useEffect(() => {
    const el = box.current;
    if (el === null) return;
    const ro = new ResizeObserver(([e]) => { if (e !== undefined) setWidth(Math.max(240, e.contentRect.width)); });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  useEffect(() => {
    if (menu === null) return;
    const close = (e: globalThis.KeyboardEvent) => { if (e.key === "Escape") setMenu(null); };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [menu]);

  const unit = field === null ? "" : unitFor(field, distanceKm);
  // W123: a fixed range's days, today whole (its scale ends ON today's position); `All` describes the whole history.
  const window_ = summaryWindow(days, dates, now);
  const sum = field === null ? null : summary(points, selected, window_);
  const pr = personalRecord(points);
  const choices = RANGES.filter((r) => isUnlocked(r, tenure));

  const jumpToRecord = () => {
    if (pr === null) return;
    if (days === null) setScroll(scrollPosition(pr.date, null, dates, now));
    else {
      if (!isShown(pr.date, days, dates, now)) { pendingCentre.current = pr.date; setStoredRange("all"); writeStored(RANGE_KEY, "all"); }
    }
    setSelected(pr.date);
  };

  return (
    <section className="pchart" aria-labelledby={titleId} ref={root}>
      <header className="pchart-head">
        <VideoThumb demoVideoUrl={demoVideoUrl} thumbnailUrl={thumbnailUrl} size="row" />
        <h3 id={titleId} className="pchart-title">{history.name}</h3>
        <button type="button" className="pchart-close" onClick={onClose} aria-label={`Close ${history.name}'s chart`}>
          <Icon name="x" size={14} />
        </button>
      </header>

      <div className="pchart-range">
        {choices.length > 1 ? (
          <div className="pchart-menu-anchor">
            <button type="button" className="pchart-range-button" aria-haspopup="listbox" aria-expanded={menu === "range"}
                    aria-label={`Time range: ${rangeLongLabel(range)}`} onClick={() => setMenu(menu === "range" ? null : "range")}>
              {rangeLongLabel(range)} <Chevron />
            </button>
            {menu === "range" && (
              <ul className="pchart-menu is-centred" role="listbox" aria-label="Time range">
                {choices.map((r) => (
                  <li key={r} role="option" aria-selected={r === range}>
                    <button type="button" onClick={() => { setStoredRange(r); writeStored(RANGE_KEY, r); setMenu(null); setSelected(null); }}>
                      <span className="pchart-tick" aria-hidden="true">{r === range ? "✓" : ""}</span>{rangeLongLabel(r)}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <span className="pchart-range-button is-static">{rangeLongLabel(range)}</span>
        )}
      </div>

      <div className="pchart-card" ref={box}>
        {field !== null && points.length > 0 && sum !== null ? (
          <>
            <div className="pchart-headline">
              <span className="pchart-value">{plus}{numberText(sum.value)}</span>
              <span className="pchart-unit">{unit}</span>
              {sum.delta !== null && Math.abs(sum.delta) > 0.0001 && (
                <span className={`pchart-delta${sum.delta > 0 ? " is-up" : ""}`}>
                  <Icon name={sum.delta > 0 ? "arrow-up-right" : "arrow-down-right"} size={10} stroke={2.2} />
                  {numberText(Math.abs(sum.delta))}
                </span>
              )}
            </div>
            <p className="pchart-caption">{headlineCaption(field, sum, selected)}</p>
            <p className="pchart-axis-unit" aria-label={`Vertical axis: ${axisUnitLabel(unit)}`}>{axisUnitLabel(unit)}</p>
            <Plot width={width} points={points} field={field} days={days} tenure={tenure} now={now}
                  scroll={scroll} setScroll={setScroll} selected={selected} setSelected={setSelected}
                  scrub={scrub} setScrub={setScrub} label={`${fieldView(field).label} over ${rangeLongLabel(range).toLowerCase()}`} />
            <p className="pchart-axis-date">Date</p>
          </>
        ) : needsBodyweight ? (
          <div className="pchart-empty">
            <p className="pchart-empty-title">{NO_BODYWEIGHT_COACH}</p>
          </div>
        ) : (
          <div className="pchart-empty">
            <p className="pchart-empty-title">Nothing to chart yet</p>
            <p className="pchart-empty-reason">{emptyReason(history.name, entries.length, available, field)}</p>
          </div>
        )}
        {(available.length > 1 || points.length > 0) && (
          <>
            <hr className="pchart-rule" />
            <div className="pchart-foot">
              {available.length > 1 && field !== null && (
                <div className="pchart-menu-anchor">
                  <button type="button" className="pchart-field" aria-haspopup="listbox" aria-expanded={menu === "field"}
                          onClick={() => setMenu(menu === "field" ? null : "field")}>
                    <FieldIcon field={field} /> {fieldView(field).label} <Chevron />
                  </button>
                  {menu === "field" && (
                    <ul className="pchart-menu" role="listbox" aria-label="What the chart plots">
                      {available.map((f) => (
                        <li key={f} role="option" aria-selected={f === field}>
                          <button type="button" onClick={() => { setStoredField(f); writeStored(FIELD_KEY, f); setSelected(null); setMenu(null); }}>
                            <span className="pchart-tick" aria-hidden="true">{f === field ? "✓" : ""}</span>{fieldView(f).longLabel}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              <span className="pchart-foot-gap" />
              {pr !== null && field !== null && (
                <button type="button" className="pchart-pr" onClick={jumpToRecord}
                        aria-label={`Personal record ${plus}${prLine(points, unit)?.slice(3) ?? ""} — shows it on the chart`}>
                  <Medal /> {plus === "" ? prLine(points, unit) : prLine(points, unit)?.replace(/^PR /, "PR +")}
                </button>
              )}
            </div>
            {points.length > 0 && (
              <Readout history={history} selected={selected} notesByInstance={notesByInstance} clipsByInstance={clipsByInstance} distanceKm={distanceKm} />
            )}
          </>
        )}
      </div>
      {/* W125 part 2 (b) — the tested max is a fact about the LIFT, not the plotted field: under the card, 1RM on only. */}
      {testedMaxClientId !== undefined && oneRM.kind === "coach" && oneRM.enabled && (
        <TestedMaxBar clientId={testedMaxClientId} exerciseId={history.id} rule={oneRM} entries={entries} weighIns={weighIns} now={now} />
      )}
    </section>
  );
}

function Readout({ history, selected, notesByInstance, clipsByInstance, distanceKm }: {
  history: ExerciseHistory; selected: number | null; distanceKm: boolean;
  notesByInstance?: ReadonlyMap<string, readonly { setNumber: number | null; text: string }[]>;
  clipsByInstance?: ReadonlyMap<string, readonly Clip[]>;
}) {
  const onDay = selected === null ? [] : history.entries.filter((e) => sameDay(e.date, selected));
  if (selected === null || onDay.length === 0) {
    return <p className="pchart-hint">{hint(history.entries.length)}</p>;
  }
  return (
    <div className="pchart-readout" aria-live="polite">
      <p className="pchart-readout-day">
        {sessionDay(selected)}
      </p>
      {onDay.map((e) => (
        <div key={e.exerciseInstanceId}>
          {/* W116 — the entry's clips behind the ONE play button, on its own line (a summary line: the stage opens on the
              first clip and walks the rest in set order). No clip, nothing drawn. */}
          <p className="pchart-readout-line">
            {readoutLine(e.performed, distanceKm)}
            <ClipPlayButton clips={clipsOfLine(clipsByInstance, [e.exerciseInstanceId])} setWords={history.name} setLabel={null} />
          </p>
          {(notesByInstance?.get(e.exerciseInstanceId) ?? []).map((n, i) => (
            <p key={i} className="pchart-readout-note">{n.setNumber !== null ? `Set ${n.setNumber}: ` : ""}{n.text}</p>
          ))}
        </div>
      ))}
    </div>
  );
}

function Plot({ width, points, field, days, tenure, now, scroll, setScroll, selected, setSelected, scrub, setScrub, label }: {
  width: number; points: readonly { date: number; value: number }[]; field: Field; days: number | null; tenure: number;
  now: number; scroll: number; setScroll: (n: number) => void; selected: number | null; setSelected: (n: number | null) => void;
  scrub: number | null; setScrub: (n: number | null) => void; label: string;
}) {
  const dates = points.map((p) => p.date);
  const integral = isIntegral(field);
  // W121 — one scale for every range: a fixed range ends TODAY and does not move; `All` slides a 61-day window.
  const full = fullDomain(dates, days, now);
  const slides = isScrollable(days, dates, now);
  const windowMs = visibleLength(days, dates, now);
  // W123: the plotted domain IS the visible window, edge to edge (END_PADDING 0).
  const vis = visibleWindow(days, dates, now, scroll);
  const bounds = scrollBounds(days, dates, now);
  const yd = yDomain(points.map((p) => p.value), integral);
  const ticks = yTicks(yd.lower, yd.upper, yTickCount(yd, integral), integral);
  const tickText = (v: number) => (integral ? String(Math.round(v)) : numberText(v));   // nice ticks: 32 · 2.5 · 0.5
  const fr = frame(width, Math.max(...ticks.map((t) => tickText(t).length), 1));
  const x = xScale(fr, vis.lower, vis.upper);
  const y = yScale(fr, yd.lower, yd.upper);
  const rule = labelRule(days, labelSpanDays(days, tenure));
  // W123 (D): labels under their day's position, the END labels hanging inward, F2 for an interior label of a slid window,
  // a crowding neighbour of an end yielding — `axisLabels`, the twin of the Swift rule.
  const labels = axisLabels(labelDates(full, rule), vis, fr.plotRight - fr.plotLeft, rule);
  // F3: a fixed range draws only its own days' sessions (`isShown`); W123: the curve is the window's points at day positions
  // plus a point ON each edge the history runs past (`windowed`); the DOTS are the real sessions inside the window.
  const shown = points.filter((p) => isShown(p.date, days, dates, now));
  const curve = windowed(shown, vis).map((p) => ({ x: x.at(p.date), y: y(p.value), date: p.date }));
  const xy = shown.filter((p) => plotX(p.date) >= vis.lower && plotX(p.date) <= vis.upper)
    .map((p) => ({ x: x.at(plotX(p.date)), y: y(p.value), date: p.date }));
  const baseline = y(yd.lower);
  const dots = showsDots(days);
  const clip = useId().replace(/:/g, "");
  const drag = useRef<{ x0: number; s0: number } | null>(null);
  const perPx = windowMs / Math.max(1, fr.plotRight - fr.plotLeft);
  const cursor = scrub ?? (selected !== null && dates.some((d) => sameDay(d, selected)) ? selected : null);
  const hasEarlier = slides && vis.lower > bounds.lower + 3_600_000;
  const hasLater = slides && vis.lower < bounds.upper - 3_600_000;

  const local = (e: MouseEvent<SVGRectElement>) => {
    const r = (e.currentTarget.ownerSVGElement ?? e.currentTarget).getBoundingClientRect();
    return e.clientX - r.left;
  };
  const onKey = (e: KeyboardEvent<SVGSVGElement>) => {
    if (e.key === "Escape") { setSelected(null); return; }
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    e.preventDefault();
    const i = selected === null ? (e.key === "ArrowLeft" ? dates.length : -1) : dates.findIndex((d) => sameDay(d, selected));
    const next = dates[Math.min(dates.length - 1, Math.max(0, i + (e.key === "ArrowLeft" ? -1 : 1)))];
    if (next === undefined) return;
    setSelected(next);
    if (slides && (next < vis.lower || next > vis.upper)) setScroll(scrollPosition(next, days, dates, now));
  };

  return (
    <svg className="pchart-plot" width={width} height={PLOT_HEIGHT} viewBox={`0 0 ${width} ${PLOT_HEIGHT}`} role="img"
         aria-label={label} tabIndex={0} onKeyDown={onKey}>
      <defs>
        <clipPath id={`c${clip}`}><rect x={fr.plotLeft} y={0} width={fr.plotRight - fr.plotLeft} height={PLOT_HEIGHT} /></clipPath>
        <linearGradient id={`g${clip}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" className="pchart-fill-top" />
          <stop offset="1" className="pchart-fill-bottom" />
        </linearGradient>
      </defs>
      {slides && (
        <g className="pchart-strip">
          <rect x={fr.plotLeft} y={PLOT_HEIGHT - DATE_BAND} width={fr.plotRight - fr.plotLeft} height={DATE_BAND} rx={9} />
          {hasEarlier && <path className="pchart-strip-chevron" d={`M${fr.plotLeft - 8},${PLOT_HEIGHT - 17}l-4,4l4,4`} />}
          {hasLater && <path className="pchart-strip-chevron" d={`M${fr.plotRight - 6},${PLOT_HEIGHT - 17}l4,4l-4,4`} />}
        </g>
      )}
      {ticks.map((t) => (
        <g key={t} className="pchart-ytick">
          <line x1={fr.plotLeft} x2={fr.plotRight} y1={y(t)} y2={y(t)} />
          <line x1={fr.plotLeft - TICK} x2={fr.plotLeft} y1={y(t)} y2={y(t)} />
          <text x={fr.plotLeft - TICK - AXIS_GAP + 4} y={y(t)} dy="0.35em" textAnchor="end">{tickText(t)}</text>
        </g>
      ))}
      <line className="pchart-axis" x1={fr.plotLeft} x2={fr.plotLeft} y1={0} y2={fr.plotBottom} />
      <line className="pchart-axis" x1={fr.plotLeft} x2={fr.plotRight} y1={fr.plotBottom} y2={fr.plotBottom} />
      {labels.map((l) => (
        <g key={l.date} className="pchart-xtick">
          <line x1={x.at(l.date)} x2={x.at(l.date)} y1={fr.plotBottom} y2={fr.plotBottom + TICK} />
          <text x={x.at(l.date)} y={fr.plotBottom + 17}
                textAnchor={l.anchor === "leading" ? "start" : l.anchor === "trailing" ? "end" : "middle"}>{axisLabel(l.date, rule)}</text>
        </g>
      ))}
      <g clipPath={`url(#c${clip})`}>
        {curve.length > 1 && <path className="pchart-area" d={areaPath(curve, baseline)} fill={`url(#g${clip})`} />}
        {curve.length > 1 && <path className="pchart-line" d={monotonePath(curve)} />}
        {cursor !== null && (
          <line className={`pchart-cursor${scrub !== null ? " is-scrubbing" : ""}`} x1={x.at(scrub ?? plotX(cursor))} x2={x.at(scrub ?? plotX(cursor))} y1={0} y2={fr.plotBottom} />
        )}
      </g>
      {/* W123 (C): the dots sit OUTSIDE the clip — an edge day's dot is drawn whole, over the axis line. */}
      <g>
        {xy.map((p) => {
          const isSel = selected !== null && sameDay(p.date, selected);
          if (!dots && !isSel) return null;
          return (
            <g key={p.date} className="pchart-dot">
              {isSel && scrub === null && <circle className="pchart-dot-halo" cx={p.x} cy={p.y} r={13} />}
              <circle className="pchart-dot-ring" cx={p.x} cy={p.y} r={6} />
              <circle className="pchart-dot-core" cx={p.x} cy={p.y} r={isSel && scrub === null ? 5 : 4} />
            </g>
          );
        })}
      </g>
      <rect className="pchart-hit" x={fr.plotLeft} y={0} width={fr.plotRight - fr.plotLeft} height={fr.plotBottom}
            onPointerMove={(e) => setScrub(x.invert(local(e)))}
            onPointerLeave={() => setScrub(null)}
            onClick={(e) => {
              // W123 (E): nearest among the window's DAY positions — a dot on either edge is hit at x = 0 and x = the width.
              const inWin = dates.map(plotX).filter((d) => d >= vis.lower && d <= vis.upper);
              setSelected(nearest(x.invert(local(e)), inWin)); setScrub(null);
            }} />
      {slides && (
        <rect className="pchart-strip-hit" x={fr.plotLeft} y={PLOT_HEIGHT - DATE_BAND} width={fr.plotRight - fr.plotLeft} height={DATE_BAND}
              aria-label="Drag to move through time"
              onPointerDown={(e) => { e.currentTarget.setPointerCapture(e.pointerId); drag.current = { x0: e.clientX, s0: scroll }; }}
              onPointerMove={(e) => {
                if (drag.current === null) return;
                setScroll(Math.min(Math.max(drag.current.s0 - (e.clientX - drag.current.x0) * perPx, bounds.lower), bounds.upper));
              }}
              onPointerUp={() => {
                if (drag.current === null) return;
                drag.current = null;
                const mid = startOfDay(scroll + DAY / 2);
                setScroll(Math.min(Math.max(mid, bounds.lower), bounds.upper));
              }}
              onWheel={(e) => {
                const d = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
                setScroll(Math.min(Math.max(startOfDay(scroll + Math.sign(d) * DAY + DAY / 2), bounds.lower), bounds.upper));
              }} />
      )}
    </svg>
  );
}

/* ── the row's MINIATURE: the chart's own dots and line, no axes ─────────────────────────────────── */

export function ProgressMiniature({ history, progressViews, oneRM = ENGINE_RULE, weighIns = [] }: {
  history: ExerciseHistory; progressViews: readonly string[]; oneRM?: OneRMRule; weighIns?: readonly WeighIn[];
}) {
  const field = resolvedField(null, allowed(fieldsFor(history.entries, oneRM), progressViews, oneRM));
  const pts = field === null ? [] : seriesPoints(field, history.entries, oneRM, weighIns).slice(-8);
  const W = 96, H = 28, pad = 4;
  if (pts.length === 0) return <span className="pmini is-empty" aria-hidden="true" />;
  const lo = Math.min(...pts.map((p) => p.value)), hi = Math.max(...pts.map((p) => p.value));
  const t0 = pts[0]!.date, t1 = pts[pts.length - 1]!.date;
  const xy = pts.map((p) => ({
    x: pts.length === 1 ? W / 2 : pad + ((p.date - t0) / (t1 - t0 || 1)) * (W - 2 * pad),
    y: hi === lo ? H / 2 : H - pad - ((p.value - lo) / (hi - lo)) * (H - 2 * pad),
  }));
  return (
    <svg className="pmini" width={W} height={H} viewBox={`0 0 ${W} ${H}`} aria-hidden="true">
      {xy.length > 1 && <path className="pmini-line" d={monotonePath(xy)} />}
      {xy.map((p, i) => <circle key={i} className="pmini-dot" cx={p.x} cy={p.y} r={2.25} />)}
    </svg>
  );
}

/* ── glyphs: one 1.5 px line set (Proposal 7 rule 9), never emoji in the chrome ─────────────────── */

function Chevron() {
  return <Icon name="chevron-down" className="pchart-chevron" size={10} stroke={2.2} />;
}

function Medal() {
  return (
    <Icon name="medal" className="pchart-medal" size={16} />
  );
}

/* PRO WEB STEP 7 — the six progress views' icons are Lucide's, through the one Icon module (Fable's ruling). */
const FIELD_ICON: Record<Field, IconName> = {
  oneRM: "trophy", topSet: "dumbbell", volume: "chart", bestSet: "refresh", totalReps: "sigma",
  longestHold: "clock", timeUnderTension: "hourglass",
  longestDistance: "move", totalDistance: "sigma", mostRounds: "refresh",
};

function FieldIcon({ field }: { field: Field }) {
  return <Icon name={FIELD_ICON[field]} className="pchart-field-icon" size={16} />;
}
